/**
 * TerminalPanel Component
 *
 * Resizable bottom panel containing terminal sessions.
 * Supports two terminal types: Home (standard) and AI (with database context).
 *
 * AI Workspace sync features (schema export, file sync) require Pro mode.
 * In Personal mode, AI terminals work as basic shells without workspace sync.
 */

import { useRef, useCallback, useEffect, useState } from 'react';
import { Minus, Square, X } from 'lucide-react';
import { useTerminalStore, MIN_PANEL_HEIGHT, MAX_PANEL_HEIGHT_PERCENT, DEFAULT_PANEL_HEIGHT } from '../store/terminalStore';
import { useSplitterDrag } from '../../../layout/hooks/useSplitterDrag';
import { useAnalytics } from '../../../core/analytics/useAnalytics';
import { createTerminalSession, closeTerminalSession } from '../services/terminalApi';
import { useConnectionStore } from '../../connections/store';
import { useDatabaseUIStore } from '../../database-objects/store';
import { useConnectionsUIStore } from '../../connections/store/uiStore';
import { useHasProAccess, useProUpgradePromptStore, ProBadge } from '../../../core/licensing';
import { ConnectionSelectorDialog, DatabaseSelectorDialog } from '../../toolbar-context';
import { useAiWorkspaceSetupConsent } from '../../ai-toolbar/hooks/useAiWorkspaceSetupConsent';
import { AiWorkspaceSetupConfirmDialog } from '../../ai-toolbar/components/AiWorkspaceSetupConfirmDialog';
import { TerminalTabs } from './TerminalTabs';
import { TerminalViewHost } from './TerminalViewLazy';
import { AiInitOverlay } from './AiInitOverlay';
import { TerminalErrorNotice } from './TerminalErrorNotice';
import { AiTerminalContextBar } from './AiTerminalContextBar';
import { AiWorkspaceLargeDbDialog } from './AiWorkspaceLargeDbDialog';
import { AiWorkspacePersonalLimitMessage } from './AiWorkspacePersonalLimitMessage';
import type {
  TerminalSession,
  TerminalType,
  TerminalInitProgressEvent,
  TerminalLargeDbDetectedEvent,
  SchemaExportFilter,
} from '../../../types/terminal';
import type { ActiveConnection } from '../../connections/models/connectionTypes';
import { safeConnectionName } from '../../../types/safeConnectionDisplay';

/**
 * Terminal panel with resizable height and multi-session support
 */
/** Keyboard resize step for the terminal panel height, in pixels (Shift = 4x). */
const TERMINAL_KEY_STEP = 8;
const TERMINAL_KEY_STEP_BIG = 32;

export function TerminalPanel() {
  const containerRef = useRef<HTMLDivElement>(null);
  const [createError, setCreateError] = useState<string | null>(null);
  const { track } = useAnalytics();

  // State for AI terminal context selection flow
  const [showConnectionSelector, setShowConnectionSelector] = useState(false);
  const [showDatabaseSelector, setShowDatabaseSelector] = useState(false);
  const [pendingAiConnection, setPendingAiConnection] = useState<ActiveConnection | null>(null);

  // Store state
  const isVisible = useTerminalStore((s) => s.isVisible);
  const height = useTerminalStore((s) => s.height);
  const isMaximized = useTerminalStore((s) => s.isMaximized);
  const sessions = useTerminalStore((s) => s.sessions);
  const activeSessionId = useTerminalStore((s) => s.activeSessionId);
  const pendingAiTerminalRequest = useTerminalStore((s) => s.pendingAiTerminalRequest);
  const setHeight = useTerminalStore((s) => s.setHeight);
  const toggleMaximize = useTerminalStore((s) => s.toggleMaximize);
  const hidePanel = useTerminalStore((s) => s.hidePanel);
  const addSession = useTerminalStore((s) => s.addSession);
  const removeSession = useTerminalStore((s) => s.removeSession);
  const updateInitState = useTerminalStore((s) => s.updateInitState);
  const clearAiTerminalRequest = useTerminalStore((s) => s.clearAiTerminalRequest);

  // Connection state for creating terminals - use explicit state from store
  const activeConnections = useConnectionStore((s) => s.activeConnections);
  const selectedConnectionId = useDatabaseUIStore((s) => s.selectedConnectionId);
  const selectedDatabase = useDatabaseUIStore((s) => s.selectedDatabase);

  // Get the selected connection using explicit state (not parsing nodeId)
  const getSelectedConnection = useCallback(() => {
    const connectedConnections = activeConnections.filter((c) => c.status === 'connected');
    if (selectedConnectionId) {
      return connectedConnections.find((c) => c.id === selectedConnectionId) ?? null;
    }
    // Fall back to first active connection if nothing selected
    return connectedConnections[0] ?? null;
  }, [selectedConnectionId, activeConnections]);

  // Check if AI workspace features are available (Pro access - reactive)
  const canUseAiWorkspace = useHasProAccess();
  const showUpgradePrompt = useProUpgradePromptStore((s) => s.showPrompt);
  const isUpgradeDialogOpen = useProUpgradePromptStore((s) => s.isOpen);

  // Large DB state from store
  const largeDbEvent = useTerminalStore((s) => s.largeDbEvent);
  const setLargeDbEvent = useTerminalStore((s) => s.setLargeDbEvent);

  // State for showing personal mode limit message (modal-like flow)
  const [showPersonalLimitMessage, setShowPersonalLimitMessage] = useState(false);

  // Track whether upgrade dialog was open to detect when it closes
  const wasUpgradeDialogOpenRef = useRef(false);

  // Check if we have any connected connections for AI terminal
  const connectedConnections = activeConnections.filter((c) => c.status === 'connected');

  // Get the active session for status bar
  const activeSession = sessions.find((s) => s.id === activeSessionId);

  // Create AI terminal with specific connection and database
  const createAiTerminalWithContext = useCallback(async (
    connection: ActiveConnection,
    database: string | undefined
  ) => {
    try {
      setCreateError(null);
      const result = await createTerminalSession({
        type: 'ai',
        connectionId: connection.id,
        database,
      });

      if (result.success && result.sessionId) {
        const connInfo = connection.connection;
        const session: TerminalSession = {
          id: result.sessionId,
          title: database
            ? `AI: ${safeConnectionName(connInfo)} - ${database}`
            : `AI: ${safeConnectionName(connInfo)}`,
          type: 'ai',
          connectionId: connection.id,
          database,
          workspacePath: result.workspacePath,
          createdAt: Date.now(),
          // Only seed an optimistic "Initializing…" state when the main
          // process actually scheduled a schema export for this session —
          // otherwise no `terminal:init-progress` event will ever arrive to
          // move it out of 'starting', leaving the context/status bars stuck
          // forever (e.g. mock engine, exportSchemaOnOpen off, unsupported
          // engine, no database). Leaving initState undefined shows the
          // normal ready state immediately instead.
          initState: result.schemaExportScheduled
            ? { phase: 'starting', message: 'Initializing AI workspace...' }
            : undefined,
        };
        addSession(session);
      } else {
        console.error('[TerminalPanel] Failed to create AI terminal:', result.error);
        setCreateError(result.error || 'Failed to create terminal session.');
      }
    } catch (err) {
      console.error('[TerminalPanel] Error creating AI terminal:', err);
      setCreateError(err instanceof Error ? err.message : 'Unexpected error creating terminal session.');
    }
  }, [addSession]);

  // Shared AI-workspace consent gate ([D1→D2] follow-up) — TerminalPanel's own
  // AI-terminal creation surfaces (the "+ AI Context" button and the
  // connection-selector flow below) used to call createAiTerminalWithContext
  // directly, bypassing the consent dialog AiToolbarButton already gates
  // behind. Every surface below now goes through requestAiWorkspaceSetup
  // instead, which shows AiWorkspaceSetupConfirmDialog (rendered further down)
  // when `confirmBeforeSetup` is on, and creates immediately when it's off.
  const {
    consentContext,
    requestSetup: requestAiWorkspaceSetup,
    handleConfirm: handleConsentConfirm,
    handleCancel: handleConsentCancel,
  } = useAiWorkspaceSetupConsent(createAiTerminalWithContext);

  // Handle connection selection from dialog
  const handleConnectionSelected = useCallback((connectionId: string) => {
    const connection = activeConnections.find((c) => c.id === connectionId);
    if (!connection) return;

    setShowConnectionSelector(false);
    setPendingAiConnection(connection);
    // Show database selector next
    setShowDatabaseSelector(true);
  }, [activeConnections]);

  // Handle database selection from dialog
  const handleDatabaseSelected = useCallback((database: string) => {
    setShowDatabaseSelector(false);
    if (pendingAiConnection) {
      requestAiWorkspaceSetup(pendingAiConnection, database);
      setPendingAiConnection(null);
    }
  }, [pendingAiConnection, requestAiWorkspaceSetup]);

  // Handle closing database selector without selection (use connection only)
  const handleDatabaseDialogClose = useCallback((open: boolean) => {
    if (!open) {
      setShowDatabaseSelector(false);
      // Create terminal with connection only (no specific database)
      if (pendingAiConnection) {
        requestAiWorkspaceSetup(pendingAiConnection, undefined);
        setPendingAiConnection(null);
      }
    }
  }, [pendingAiConnection, requestAiWorkspaceSetup]);

  // Handle "Manage Connections" from selector
  const handleManageConnections = useCallback(() => {
    setShowConnectionSelector(false);
    useConnectionsUIStore.getState().setShowConnectionsDialog(true);
  }, []);

  // Handle retry schema export for AI terminal
  const handleRetryExport = useCallback(async (sessionId: string, connectionId: string, database?: string) => {
    try {
      const result = await window.electronAPI?.terminal.retryExport({
        sessionId,
        connectionId,
        database,
      });
      if (!result?.success) {
        console.error('[TerminalPanel] Failed to retry schema export:', result?.error);
      }
    } catch (err) {
      console.error('[TerminalPanel] Error retrying schema export:', err);
    }
  }, []);

  // Create a new terminal session
  const handleCreateTerminal = useCallback(async (type: TerminalType) => {
    try {
      setCreateError(null);
      if (type === 'home') {
        // Home terminal - no connection required
        const result = await createTerminalSession({
          type: 'home',
        });

        if (result.success && result.sessionId) {
          const session: TerminalSession = {
            id: result.sessionId,
            title: 'Terminal',
            type: 'home',
            createdAt: Date.now(),
          };
          addSession(session);
        } else {
          console.error('[TerminalPanel] Failed to create home terminal:', result.error);
          setCreateError(result.error || 'Failed to create terminal session.');
        }
      } else {
        // AI terminal - check if connection is already selected
        const connection = getSelectedConnection();

        if (!connection) {
          // No connection selected - check if we have connected connections
          if (connectedConnections.length === 0) {
            // No connections at all - open connections dialog
            useConnectionsUIStore.getState().setShowConnectionsDialog(true);
            return;
          }
          // Show connection selector
          setShowConnectionSelector(true);
          return;
        }

        // Connection is selected - go through the consent gate
        requestAiWorkspaceSetup(connection, selectedDatabase);
      }
    } catch (err) {
      console.error('[TerminalPanel] Error creating terminal:', err);
      setCreateError(err instanceof Error ? err.message : 'Unexpected error creating terminal session.');
    }
  }, [getSelectedConnection, selectedDatabase, addSession, connectedConnections, requestAiWorkspaceSetup]);

  // Resize handle (CH-13): pointer-capture + rAF drag, keyboard step,
  // double-click reset, via the shared `useSplitterDrag` hook (also used by
  // the app-shell sidebar/vertical-tabs seams). The panel div's height is
  // mutated directly during the drag (see `onDrag`) and only committed to
  // the store on drag end, matching the pre-existing mouse-drag behaviour
  // this replaces. `isMaximized` guards the visual write only — the pending
  // height is still tracked and committed at drag end, matching the
  // pre-existing behaviour where dragging while maximized silently updated
  // `height` with no visible effect (maximize ignores it). Without that
  // guard, a direct DOM write would visibly resize a maximized panel during
  // the drag, then snap back to the maximized size on release — a new
  // glitch the old per-mousemove `setHeight` never had, since maximized
  // renders always ignored `height` for the visual anyway. This also
  // matches standard OS window-maximize semantics (a maximized window can't
  // be drag-resized either).
  const terminalHandleRef = useRef<HTMLDivElement>(null);
  const terminalDragStartHeightRef = useRef(height);
  const terminalMaxHeightRef = useRef(0);
  const terminalPendingHeightRef = useRef(height);
  const terminalSplitter = useSplitterDrag({
    axis: 'y',
    onDrag: (deltaPx) => {
      // Dragging up increases height (mirrors the original `startY - clientY`).
      const newHeight = Math.max(
        MIN_PANEL_HEIGHT,
        Math.min(terminalMaxHeightRef.current, terminalDragStartHeightRef.current - deltaPx)
      );
      terminalPendingHeightRef.current = newHeight;
      if (isMaximized) return;
      const el = containerRef.current;
      if (!el) return;
      el.style.height = `${newHeight}px`;
      const handle = terminalHandleRef.current;
      if (handle) {
        const rounded = Math.round(newHeight);
        handle.setAttribute('aria-valuenow', String(rounded));
        handle.setAttribute('aria-valuetext', `${rounded} pixels`);
      }
    },
    onDragEnd: () => {
      setHeight(terminalPendingHeightRef.current);
    },
    onReset: () => {
      setHeight(DEFAULT_PANEL_HEIGHT);
      track('feature:splitter_reset', { surface: 'terminal-panel' });
    },
    onKeyStep: (direction, big) => {
      const step = big ? TERMINAL_KEY_STEP_BIG : TERMINAL_KEY_STEP;
      const maxHeight = window.innerHeight * (MAX_PANEL_HEIGHT_PERCENT / 100);
      setHeight(Math.max(MIN_PANEL_HEIGHT, Math.min(maxHeight, height + direction * step)));
    },
  });

  // Handle maximize toggle
  const handleToggleMaximize = useCallback(() => {
    toggleMaximize();
  }, [toggleMaximize]);

  // Listen for AI workspace init progress events
  useEffect(() => {
    const cleanup = window.electronAPI?.terminal.onInitProgress(
      (event: TerminalInitProgressEvent) => {
        updateInitState(event.sessionId, {
          phase: event.phase,
          message: event.message,
          currentItem: event.currentItem,
          progress: event.progress,
        });

        // Clear init state after a short delay when complete
        if (event.phase === 'complete') {
          setTimeout(() => {
            updateInitState(event.sessionId, undefined);
          }, 2000);
        }
      }
    );

    return () => {
      cleanup?.();
    };
  }, [updateInitState]);

  // Listen for large database detected events
  useEffect(() => {
    const cleanup = window.electronAPI?.terminal.onLargeDbDetected(
      (event: TerminalLargeDbDetectedEvent) => {
        console.log('[TerminalPanel] Large database detected:', event);

        if (canUseAiWorkspace) {
          // Pro mode: Show filter dialog
          setLargeDbEvent(event);
        } else {
          // Personal mode: Show limit message
          setLargeDbEvent(event);
          setShowPersonalLimitMessage(true);
        }
      }
    );

    return () => {
      cleanup?.();
    };
  }, [setLargeDbEvent, canUseAiWorkspace]);

  // Handle Pro filter confirmation
  const handleLargeDbFilterConfirm = useCallback(async (filter: SchemaExportFilter) => {
    if (!largeDbEvent) return;

    try {
      const result = await window.electronAPI?.terminal.confirmFilter({
        sessionId: largeDbEvent.sessionId,
        filter,
      });

      if (!result?.success) {
        console.error('[TerminalPanel] Failed to confirm filter:', result?.error);
      }
    } catch (err) {
      console.error('[TerminalPanel] Error confirming filter:', err);
    } finally {
      setLargeDbEvent(null);
    }
  }, [largeDbEvent, setLargeDbEvent]);

  // Handle Pro filter cancellation (use default filter: all limited by MAX)
  const handleLargeDbFilterCancel = useCallback(async () => {
    if (!largeDbEvent) return;

    try {
      // Cancel = use default 'all' filter (will be limited by MAX_OBJECTS_PER_TYPE)
      const result = await window.electronAPI?.terminal.confirmFilter({
        sessionId: largeDbEvent.sessionId,
        filter: {
          tables: 'all',
          views: 'all',
          procedures: 'all',
          functions: 'all',
        },
      });

      if (!result?.success) {
        console.error('[TerminalPanel] Failed to confirm default filter:', result?.error);
      }
    } catch (err) {
      console.error('[TerminalPanel] Error confirming default filter:', err);
    } finally {
      setLargeDbEvent(null);
    }
  }, [largeDbEvent, setLargeDbEvent]);

  // Handle Personal mode continue (use default filter limited to 100 per type)
  const handlePersonalContinue = useCallback(async () => {
    setShowPersonalLimitMessage(false);

    if (!largeDbEvent) return;

    try {
      // Personal mode: use 'all' filter (will be limited by MAX_OBJECTS_PER_TYPE = 125)
      const result = await window.electronAPI?.terminal.confirmFilter({
        sessionId: largeDbEvent.sessionId,
        filter: {
          tables: 'all',
          views: 'all',
          procedures: 'all',
          functions: 'all',
        },
      });

      if (!result?.success) {
        console.error('[TerminalPanel] Failed to confirm personal filter:', result?.error);
      }
    } catch (err) {
      console.error('[TerminalPanel] Error confirming personal filter:', err);
    } finally {
      setLargeDbEvent(null);
    }
  }, [largeDbEvent, setLargeDbEvent]);

  // Handle Personal mode upgrade click
  // Note: We DON'T clear largeDbEvent here - we keep it so the flow can resume
  // if the user cancels the upgrade dialog
  const handlePersonalUpgrade = useCallback(() => {
    setShowPersonalLimitMessage(false);
    showUpgradePrompt('ai-workspace-full-selection');
  }, [showUpgradePrompt]);

  // Handle Personal mode cancel - close the terminal session without starting AI workspace
  const handlePersonalCancel = useCallback(async () => {
    setShowPersonalLimitMessage(false);

    if (!largeDbEvent) return;

    const sessionId = largeDbEvent.sessionId;
    setLargeDbEvent(null);

    // Close the terminal session
    try {
      await closeTerminalSession({ sessionId });
    } catch (err) {
      console.error('[TerminalPanel] Error closing session on cancel:', err);
    }

    // Remove from store
    removeSession(sessionId);
  }, [largeDbEvent, setLargeDbEvent, removeSession]);

  // Re-show personal limit message when upgrade dialog closes without upgrading
  // This handles the case where user clicks "Upgrade", then cancels the dialog
  useEffect(() => {
    // Track when upgrade dialog transitions from open to closed
    if (wasUpgradeDialogOpenRef.current && !isUpgradeDialogOpen) {
      // Dialog just closed - check if we still have a pending large DB event
      // and user is still in personal mode (hasn't upgraded)
      if (largeDbEvent && !canUseAiWorkspace) {
        setShowPersonalLimitMessage(true);
      }
    }
    wasUpgradeDialogOpenRef.current = isUpgradeDialogOpen;
  }, [isUpgradeDialogOpen, largeDbEvent, canUseAiWorkspace]);

  // Handle pending AI terminal request from store
  useEffect(() => {
    if (!pendingAiTerminalRequest) return;

    // Clear the request first to avoid re-triggering
    clearAiTerminalRequest();

    // Get selected connection
    const connection = getSelectedConnection();

    if (!connection) {
      // No connection selected - check if we have connected connections
      if (connectedConnections.length === 0) {
        // No connections at all - open connections dialog
        useConnectionsUIStore.getState().setShowConnectionsDialog(true);
        return;
      }
      // Show connection selector
      setShowConnectionSelector(true);
      return;
    }

    // Connection is selected - go through the consent gate
    requestAiWorkspaceSetup(connection, selectedDatabase);
  }, [
    pendingAiTerminalRequest,
    clearAiTerminalRequest,
    getSelectedConnection,
    connectedConnections.length,
    selectedDatabase,
    requestAiWorkspaceSetup,
  ]);

  // Calculate actual panel height
  const panelHeight = isMaximized
    ? window.innerHeight * (MAX_PANEL_HEIGHT_PERCENT / 100)
    : height;

  // Don't render if not visible
  if (!isVisible) {
    return null;
  }

  return (
    <div
      ref={containerRef}
      className="flex flex-col border-t border-border bg-background"
      style={{ height: panelHeight }}
      data-testid="terminal-panel"
    >
      {/* Resize handle. CH-13: 1px `--divider` hairline + 6px invisible hit
          area, hover/drag pinned to the identical `--primary` tint,
          pointer-capture + rAF drag (via `useSplitterDrag`), keyboard
          resize, double-click reset, `role="separator"`. */}
      <div
        ref={terminalHandleRef}
        className="h-1.5 cursor-row-resize bg-transparent before:absolute before:inset-x-0 before:top-1/2 before:-translate-y-1/2 before:h-px before:bg-divider before:transition-colors before:content-[''] hover:before:bg-primary/60 data-[dragging=true]:before:bg-primary/60 relative outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1 focus-visible:ring-offset-background"
        style={{ touchAction: 'none', userSelect: 'none' }}
        role="separator"
        aria-orientation="horizontal"
        aria-label="Resize terminal panel"
        aria-valuenow={Math.round(height)}
        aria-valuemin={MIN_PANEL_HEIGHT}
        aria-valuemax={Math.round(window.innerHeight * (MAX_PANEL_HEIGHT_PERCENT / 100))}
        aria-valuetext={`${Math.round(height)} pixels`}
        tabIndex={0}
        data-dragging={terminalSplitter.isDragging ? 'true' : undefined}
        data-testid="terminal-resize-handle"
        onPointerDown={(e) => {
          terminalDragStartHeightRef.current = height;
          terminalMaxHeightRef.current = window.innerHeight * (MAX_PANEL_HEIGHT_PERCENT / 100);
          terminalPendingHeightRef.current = height;
          terminalSplitter.onPointerDown(e);
        }}
        onDoubleClick={terminalSplitter.onDoubleClick}
        onKeyDown={terminalSplitter.onKeyDown}
      />

      {/* Header with tabs and controls */}
      <div className="flex items-center justify-between border-b border-border bg-muted/20">
        <TerminalTabs
          onCreateTerminal={handleCreateTerminal}
          canUseAiWorkspace={canUseAiWorkspace}
        />

        {/* Panel controls */}
        <div className="flex items-center gap-1 px-2">
          <button
            className="p-1 hover:bg-muted/50 rounded transition-colors"
            onClick={hidePanel}
            title="Minimize"
          >
            <Minus className="h-3.5 w-3.5" />
          </button>
          <button
            className="p-1 hover:bg-muted/50 rounded transition-colors"
            onClick={handleToggleMaximize}
            title={isMaximized ? 'Restore' : 'Maximize'}
          >
            <Square className="h-3.5 w-3.5" />
          </button>
          <button
            className="p-1 hover:bg-muted/50 rounded transition-colors"
            onClick={hidePanel}
            title="Close"
          >
            <X className="h-3.5 w-3.5" />
          </button>
        </div>
      </div>

      {createError && (
        <div className="border-b border-border bg-muted/10 px-2 py-2">
          <TerminalErrorNotice error={createError} onDismiss={() => setCreateError(null)} />
        </div>
      )}

      {/* AI Terminal Context Bar */}
      {activeSession?.type === 'ai' && (
        <AiTerminalContextBar session={activeSession} />
      )}

      {/* Terminal content */}
      <div className="flex-1 min-h-0 overflow-hidden flex flex-col">
        {sessions.length === 0 ? (
          <div className="h-full flex flex-col items-center justify-center text-muted-foreground gap-2">
            <p className="text-sm">Click a button above to create a new terminal:</p>
            <p className="text-xs text-muted-foreground/70">
              <strong>Home</strong> — Standard terminal in home directory
              <br />
              <strong>AI Context</strong> —{' '}
              {canUseAiWorkspace ? (
                <>AI workspace with database context</>
              ) : (
                <>
                  Basic AI shell{' '}
                  <span className="inline-flex items-center gap-1">
                    (<ProBadge className="inline" /> for workspace sync)
                  </span>
                </>
              )}
            </p>
          </div>
        ) : (
          <div className="relative flex-1 min-h-0">
            {sessions.map((session) => (
              <TerminalViewHost
                key={session.id}
                sessionId={session.id}
                isActive={session.id === activeSessionId}
              />
            ))}
            {/* AI initialization overlay for active session */}
            {activeSession?.type === 'ai' && activeSession.initState && activeSession.connectionId && (
              <AiInitOverlay
                sessionId={activeSession.id}
                initState={activeSession.initState}
                onRetry={() => handleRetryExport(
                  activeSession.id,
                  activeSession.connectionId!,
                  activeSession.database
                )}
              />
            )}
          </div>
        )}
      </div>

      {/* Connection selector dialog for AI terminal */}
      <ConnectionSelectorDialog
        open={showConnectionSelector}
        onOpenChange={setShowConnectionSelector}
        connections={connectedConnections}
        onSelect={handleConnectionSelected}
        onManageConnections={handleManageConnections}
      />

      {/* Database selector dialog for AI terminal */}
      {pendingAiConnection && (
        <DatabaseSelectorDialog
          open={showDatabaseSelector}
          onOpenChange={handleDatabaseDialogClose}
          connectionId={pendingAiConnection.id}
          connectionName={safeConnectionName(pendingAiConnection.connection)}
          engine={(pendingAiConnection.connection.engine || 'mssql') as 'mssql' | 'pgsql' | 'sqlite'}
          onSelect={handleDatabaseSelected}
        />
      )}

      {/* AI workspace setup consent dialog ([D1→D2] parity — shared with AiToolbarButton) */}
      {consentContext && (
        <AiWorkspaceSetupConfirmDialog
          open
          connection={consentContext.connection}
          initialDatabase={consentContext.database}
          onConfirm={handleConsentConfirm}
          onCancel={handleConsentCancel}
        />
      )}

      {/* Large database filter dialog for Pro users */}
      {largeDbEvent && canUseAiWorkspace && !showPersonalLimitMessage && (
        <AiWorkspaceLargeDbDialog
          isOpen={true}
          sessionId={largeDbEvent.sessionId}
          connectionId={largeDbEvent.connectionId}
          databaseName={largeDbEvent.database}
          counts={largeDbEvent.counts}
          onConfirm={handleLargeDbFilterConfirm}
          onCancel={handleLargeDbFilterCancel}
        />
      )}

      {/* Large database limit message for Personal mode users */}
      {largeDbEvent && showPersonalLimitMessage && (
        <div className="absolute inset-0 z-20 flex items-center justify-center bg-background/95 backdrop-blur-sm">
          <AiWorkspacePersonalLimitMessage
            databaseName={largeDbEvent.database}
            counts={largeDbEvent.counts}
            onContinue={handlePersonalContinue}
            onUpgrade={handlePersonalUpgrade}
            onCancel={handlePersonalCancel}
          />
        </div>
      )}
    </div>
  );
}
